/**
 * Preview (coming-soon) product wishlist flow.
 *
 * <preview-buy>  Buy box on preview product pages (snippets/preview-buy-buttons.liquid):
 *                ADD TO WISHLIST saves the product to Swish, NOTIFY ME expands the drop
 *                notify form, guests get the same form as a prompt after saving, and the
 *                mobile sticky bar (snippets/preview-wishlist-bar.liquid) mirrors the button.
 * <drop-prompt>  Guest-only panel fixed to the bottom of the screen (snippets/drop-prompt.liquid).
 *                Opens when a guest saves a preview product from a Swish card heart.
 *
 * Swish readiness and item lookup come from assets/wishlist-heart.js (window.VeloSwish).
 * Unlike the heart, saves here never fall back to its localStorage store: those saves
 * would never reach Swish or Klaviyo, so a failed save shows an error instead.
 */
(() => {
  if (customElements.get('preview-buy')) return;

  const ADD_EVENTS = ['wk:wishlist:add:success', 'wishlist:add:success'];
  const CHANGE_EVENTS = [
    ...ADD_EVENTS,
    'wk:wishlist:remove:success',
    'wk:wishlist:clear:success',
    'wk:wishlist:load:success',
    'wishlist:remove:success',
    'wishlist:clear:success',
    'wishlist:load',
  ];
  const MOBILE_QUERY = '(max-width: 767px)';
  // Swish's addWishlistItem can leave its promise unsettled even after the save
  // request succeeds, so a save counts once it settles or this long passes
  // without a rejection.
  const ADD_SETTLE_MS = 3000;

  const bridge = () => window.VeloSwish || null;
  const numId = (v) => (bridge() ? bridge().numId(v) : null);

  // The Swish app instance for a save: ready now, or once whenSwishReady settles.
  // whenSwishReady memoises its timeout, so a retry after it re-checks getSwish().
  function resolveSwish() {
    const b = bridge();
    if (!b) return Promise.resolve(null);
    const wk = b.getSwish();
    return wk ? Promise.resolve(wk) : b.whenSwishReady();
  }

  // For event listeners only: keeps checking for a slow Swish boot past the
  // whenSwishReady timeout so late saves still reach the prompt.
  let swishListenerWait = null;
  function waitForSwish() {
    if (swishListenerWait) return swishListenerWait;
    swishListenerWait = resolveSwish().then((wk) => wk || new Promise((resolve) => {
      const b = bridge();
      if (!b) return resolve(null);
      let tries = 0;
      const timer = setInterval(() => {
        const found = b.getSwish();
        tries += 1;
        if (found || tries >= 60) {
          clearInterval(timer);
          resolve(found || null);
        }
      }, 1000);
    }));
    return swishListenerWait;
  }

  function subscribe(wk, names, handler) {
    if (!wk || !wk.events || typeof wk.events.subscribe !== 'function') return;
    names.forEach((name) => {
      try {
        wk.events.subscribe(name, handler);
      } catch (e) {
        /* ignore a single bad subscription */
      }
    });
  }

  function wishlistItems(wk) {
    try {
      const items = wk.state && wk.state.wishlist && wk.state.wishlist.items;
      return Array.isArray(items) ? items : null;
    } catch (e) {
      return null;
    }
  }

  // Resolves once Swish has loaded the shopper's list (or after 1s), so a very
  // early tap can't add a product that is already saved.
  function settleAdd(promise) {
    return new Promise((resolve, reject) => {
      let done = false;
      const finish = (fn, value) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        fn(value);
      };
      const timer = setTimeout(() => finish(resolve), ADD_SETTLE_MS);
      Promise.resolve(promise).then(() => finish(resolve), (err) => finish(reject, err));
    });
  }

  function listLoaded(wk) {
    if (wishlistItems(wk)) return Promise.resolve();
    return new Promise((resolve) => {
      const start = Date.now();
      const timer = setInterval(() => {
        if (wishlistItems(wk) || Date.now() - start > 1000) {
          clearInterval(timer);
          resolve();
        }
      }, 100);
    });
  }

  function itemKey(item) {
    const product = item && item.product;
    return product ? `${numId(product.id)}|${product.handle || ''}` : null;
  }

  function sameProduct(a, b) {
    return Boolean(
      (a.handle && b.handle && a.handle === b.handle) || (a.id != null && b.id != null && a.id === b.id)
    );
  }

  /* One subscription to Swish's add-to-wishlist success event, shared by every
     listener on the page. Swish documents the payload as event.data.product; if a
     build ships without it, the product is found by diffing the wishlist state
     (on the next tick, in case Swish updates its state after dispatching). */
  const addedHub = {
    listeners: new Set(),
    bound: false,
    listen(fn) {
      this.listeners.add(fn);
      if (this.bound) return;
      this.bound = true;
      waitForSwish().then((wk) => {
        if (!wk) return;
        let known = new Set();
        let knownLoaded = false;
        const snapshot = () => {
          const items = wishlistItems(wk);
          knownLoaded = Boolean(items);
          known = new Set((items || []).map(itemKey));
        };
        const notify = (added) => {
          if (added.handle || added.id != null) this.listeners.forEach((listener) => listener(added));
        };
        snapshot();
        subscribe(wk, CHANGE_EVENTS.filter((name) => !ADD_EVENTS.includes(name)), snapshot);
        subscribe(wk, ADD_EVENTS, (evt) => {
          const data = (evt && (evt.data || evt.detail)) || {};
          const product = data.product || (data.item && data.item.product) || {};
          const added = { handle: product.handle || data.productHandle || null, id: numId(product.id != null ? product.id : data.productId) };
          if (added.handle || added.id != null) {
            snapshot();
            notify(added);
            return;
          }
          setTimeout(() => {
            // A snapshot taken before the list loaded would make every old item look new.
            const fresh = knownLoaded && (wishlistItems(wk) || []).find((item) => !known.has(itemKey(item)));
            snapshot();
            if (fresh) notify({ handle: fresh.product.handle || null, id: numId(fresh.product.id) });
          }, 0);
        });
      });
    },
  };

  class PreviewBuy extends HTMLElement {
    connectedCallback() {
      if (this.bound) {
        if (this.bar) this.initBar();
        return;
      }
      this.bound = true;

      this.product = { handle: this.dataset.productHandle, id: numId(this.dataset.productId) };
      this.variantId = this.dataset.variantId || null;
      this.isGuest = this.hasAttribute('data-guest');
      this.saved = false;

      this.bar = document.querySelector(`[data-preview-bar-for="${this.id}"]`);
      const scopes = this.bar ? [this, this.bar] : [this];
      const all = (selector) => scopes.flatMap((scope) => Array.from(scope.querySelectorAll(selector)));
      this.saveButtons = all('.js-preview-save');
      this.viewLinks = all('.js-preview-view');
      this.mainButton = this.querySelector('.js-preview-save');
      this.notifyButton = this.querySelector('.js-preview-notify');
      this.formWrap = this.querySelector('.js-preview-form');

      this.saveButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.onSave(btn));
      });
      if (this.notifyButton && this.formWrap) {
        this.notifyButton.addEventListener('click', () => {
          if (this.formWrap.hidden) this.openForm({ focusInput: true });
          else this.closeForm();
        });
        this.formWrap.addEventListener('coming-soon-notify:dismiss', () => this.closeForm({ returnFocus: true }));
      }

      const productEl = this.closest('.js-product');
      if (productEl) {
        productEl.addEventListener('on:variant:change', (evt) => {
          const variant = evt.detail && evt.detail.variant;
          if (variant && variant.id != null) this.variantId = String(variant.id);
        });
      }

      addedHub.listen((added) => {
        if (!sameProduct(added, this.product)) return;
        const rect = this.getBoundingClientRect();
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;
        // Off screen (a card heart for this same product): the bottom panel prompts instead.
        this.onSaved({ fromBar: false, offScreen: !inView });
      });
      waitForSwish().then((wk) => {
        if (!wk) return;
        this.swish = wk;
        // Add events re-check on the next tick, in case Swish updates its state after dispatching.
        subscribe(wk, CHANGE_EVENTS.filter((name) => !ADD_EVENTS.includes(name)), () => this.syncSaved());
        subscribe(wk, ADD_EVENTS, () => setTimeout(() => this.syncSaved({ fromAdd: true }), 0));
        this.syncSaved();
      });

      if (this.bar) this.initBar();
    }

    async onSave(btn) {
      // Saved is final here (manage it on the wishlist page) and a save in flight
      // swallows repeat taps, so this can never add twice.
      if (this.saved || this.pending) return;
      const fromBar = this.bar ? this.bar.contains(btn) : false;
      this.setPending(true);
      this.showError(null);
      let optimistic = false;
      try {
        const wk = await resolveSwish();
        if (!wk || typeof wk.addWishlistItem !== 'function') throw new Error('Swish not ready');
        this.swish = wk;
        await listLoaded(wk);
        if (bridge().findSwishItem(wk, this.product.id, this.product.handle)) {
          this.onSaved({ fromBar });
        } else {
          const added = settleAdd(
            wk.addWishlistItem({
              productHandle: this.product.handle,
              productId: this.product.id,
              variantId: numId(this.variantId),
            })
          );
          // Show the saved state as soon as the save is sent; a rejected save undoes it.
          optimistic = true;
          this.onSaved({ fromBar });
          await added;
        }
      } catch (e) {
        if (optimistic) {
          this.saved = false;
          this.savedHere = false;
          this.render();
        }
        // Report it where the shopper is looking: the bar while it is on screen, otherwise
        // the buy box (a guest's prompt hides the bar and scrolls the buy box into view).
        const barVisible = this.bar && !this.bar.classList.contains('sticky-atc--out');
        this.showError(fromBar && barVisible ? this.bar : this);
      } finally {
        this.setPending(false);
      }
    }

    // A save from this box or any other source (e.g. Swish's add event).
    onSaved({ fromBar, offScreen = false }) {
      const wasSaved = this.saved;
      this.saved = true;
      this.savedHere = true;
      this.showError(null);
      this.render();
      const announcement = wasSaved ? [] : [`${this.mainButton.dataset.labelSaved}.`];
      if (this.isGuest && !this.prompted && this.formWrap && !offScreen) {
        this.prompted = true;
        this.openForm();
        const heading = this.formWrap.querySelector('.coming-soon-notify__heading');
        const text = this.formWrap.querySelector('.coming-soon-notify__text');
        if (heading) announcement.push(heading.textContent.trim());
        if (text) announcement.push(text.textContent.trim());
      }
      this.announce(announcement.join(' '));
      // Saved from the sticky bar: bring the buy box back so the prompt is seen,
      // and move focus off the bar, which hides once the prompt is open.
      if (fromBar && this.formWrap && !this.formWrap.hidden && window.theme && theme.scrollToRevealElement) {
        theme.scrollToRevealElement(this.formWrap);
        if (this.bar.contains(document.activeElement)) this.mainButton.focus({ preventScroll: true });
      }
    }

    // Reflect the live Swish list (saved elsewhere, removed from the wishlist page).
    syncSaved({ fromAdd = false } = {}) {
      if (!this.swish || this.pending) return;
      const items = wishlistItems(this.swish);
      if (!items) return;
      const nowSaved = Boolean(bridge().findSwishItem(this.swish, this.product.id, this.product.handle));
      // Swish's list can lag behind a save. Never let a stale read undo a save
      // made on this page (it would allow a second add).
      if ((fromAdd || this.savedHere) && this.saved && !nowSaved) return;
      this.saved = nowSaved;
      this.render();
    }

    render() {
      this.saveButtons.forEach((btn) => {
        btn.textContent = this.saved ? btn.dataset.labelSaved : btn.dataset.labelDefault;
        btn.classList.toggle('is-saved', this.saved);
        if (this.saved) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
      this.viewLinks.forEach((link) => {
        link.hidden = !this.saved;
      });
    }

    setPending(pending) {
      this.pending = pending;
      this.saveButtons.forEach((btn) => {
        if (pending) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      });
    }

    // Name changes on a focused button aren't reliably spoken, so say it.
    announce(message) {
      const status = this.querySelector('.js-preview-status');
      if (!status || !message) return;
      status.textContent = '';
      setTimeout(() => {
        status.textContent = message;
      }, 100);
    }

    // Shows the error in the tapped scope only (buy box or sticky bar), so it is
    // announced once; null clears both.
    showError(scope) {
      [this, this.bar].filter(Boolean).forEach((root) => {
        const el = root.querySelector('.js-preview-error');
        if (!el) return;
        const show = root === scope;
        el.textContent = show ? el.dataset.errorText : '';
        el.hidden = !show;
      });
    }

    openForm({ focusInput = false } = {}) {
      this.formWrap.hidden = false;
      if (this.notifyButton) this.notifyButton.setAttribute('aria-expanded', 'true');
      if (focusInput) {
        const input = this.formWrap.querySelector('.js-coming-soon-email');
        if (input && !input.closest('[hidden]')) input.focus();
      }
      this.updateBar();
    }

    closeForm({ returnFocus = false } = {}) {
      this.formWrap.hidden = true;
      if (this.notifyButton) this.notifyButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) (this.notifyButton || this.mainButton).focus();
      this.updateBar();
    }

    initBar() {
      this.mobile = window.matchMedia(MOBILE_QUERY);
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.updateBar();
        });
      };
      this.onPromptChange = () => this.updateBar();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('drop-prompt:change', this.onPromptChange);
      this.updateBar();
    }

    disconnectedCallback() {
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('drop-prompt:change', this.onPromptChange);
    }

    // Same tests as sticky-atc.js: shown once the main button is above the sticky
    // header, hidden again near the bottom of the page so it never covers the
    // footer. Also hidden on desktop and while the notify form (until subscribed)
    // or the prompt panel is open.
    updateBar() {
      if (!this.bar || !this.mobile || !this.isConnected) return;
      const header = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header') : null;
      const topOffset = header ? header.offsetHeight : 0;
      const scrolledPast = this.mainButton.getBoundingClientRect().bottom <= topOffset;
      const atBottom = window.scrollY + window.innerHeight + 100 > document.body.scrollHeight;
      const formOpen = this.formWrap && !this.formWrap.hidden && !this.formWrap.querySelector('.coming-soon-notify__message--success');
      const promptOpen = formOpen || Boolean(document.querySelector('drop-prompt.is-open'));
      const show = this.mobile.matches && scrolledPast && !atBottom && !promptOpen;
      // The theme's invisible class covers the page load; drop it the first time the bar
      // shows, by which point sticky-atc.css (and its --out state) has loaded.
      if (show) this.bar.classList.remove('invisible');
      this.bar.classList.toggle('sticky-atc--out', !show);
    }
  }

  class DropPrompt extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.notify = this.querySelector('coming-soon-notify');
      if (!this.notify) return;
      this.text = this.querySelector('.js-coming-soon-text');
      this.message = this.querySelector('.js-coming-soon-message');
      this.prompted = new Set();

      this.addEventListener('coming-soon-notify:dismiss', () => this.close());
      const closeBtn = this.querySelector('.js-drop-prompt-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      addedHub.listen((added) => this.onAdded(added));
    }

    onAdded(added) {
      // Preview product pages prompt inline under their own buttons.
      const boxes = Array.from(document.querySelectorAll('preview-buy'));
      const box = boxes.find((el) => el.product && sameProduct(added, el.product));
      if (box) {
        const rect = box.getBoundingClientRect();
        // In view: the buy box prompts inline. Off screen: this panel does.
        if (rect.bottom > 0 && rect.top < window.innerHeight) return;
      }

      const card = Array.from(document.querySelectorAll('[data-preview-product]')).find((el) =>
        sameProduct(added, { handle: el.dataset.productHandle, id: numId(el.dataset.productId) })
      );
      if (!card || this.prompted.has(card.dataset.productHandle)) return;
      this.prompted.add(card.dataset.productHandle);
      this.fill(card.dataset);
      this.open();
    }

    fill(data) {
      // A second heart save while the panel is open keeps what the guest has typed.
      const input = this.querySelector('.js-coming-soon-email');
      const typed = this.classList.contains('is-open') && input ? input.value : '';
      if (typeof this.notify.reset === 'function') this.notify.reset();
      if (typed) input.value = typed;
      this.notify.dataset.productHandle = data.productHandle || '';
      this.notify.dataset.productTitle = data.productTitle || '';
      this.notify.dataset.productUrl = data.productUrl || '';
      if (data.dropAt) this.notify.dataset.dropAt = data.dropAt;
      else delete this.notify.dataset.dropAt;
      if (this.text && data.promptText) this.text.textContent = data.promptText;
      if (this.message && data.promptSuccess) this.message.dataset.labelSuccess = data.promptSuccess;
    }

    open() {
      this.returnFocus = document.activeElement;
      this.classList.add('is-open');
      this.classList.remove('sticky-atc--out');
      // The panel doesn't take focus from the heart that opened it, so announce it.
      const status = this.querySelector('.js-drop-prompt-status');
      const heading = this.querySelector('.coming-soon-notify__heading');
      if (status && heading) {
        status.textContent = '';
        setTimeout(() => {
          status.textContent = `${heading.textContent} ${this.text ? this.text.textContent : ''}`.trim();
        }, 100);
      }
      document.dispatchEvent(new CustomEvent('drop-prompt:change'));
    }

    close() {
      const hadFocus = this.contains(document.activeElement);
      this.classList.remove('is-open');
      this.classList.add('sticky-atc--out');
      if (hadFocus) {
        if (this.returnFocus && this.returnFocus.isConnected && this.returnFocus !== document.body) this.returnFocus.focus();
        else document.activeElement.blur();
      }
      document.dispatchEvent(new CustomEvent('drop-prompt:change'));
    }
  }

  customElements.define('preview-buy', PreviewBuy);
  customElements.define('drop-prompt', DropPrompt);
})();
